import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import AccessibleNavigationAnnouncer from "./shared/components/AccessibleNavigationAnnouncer";
import TrailingSlashRedirect from "./shared/components/TrailingSlashRedirect";
import "react-toastify/dist/ReactToastify.css";
import "./style/Registration.css";
import "./style/VendorHome.css";
import "./style/Product.css";
import "./style/Order.css";
import "./style/Sidebar.css";
import "./style/FileUpload.css";
import "./style/Responsive.css";
import "./style/MultiStepForm.css";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { DateFormatProvider } from "./context/DateFormatContext";
import { ToastContainer } from "react-toastify";
import ThemedSuspense from "./shared/components/ThemedSuspense";
import { MarketingLanguageProvider } from "./features/marketing/MarketingLanguageContext";
import ProtectedRoute from "./shared/components/ProtectedRoute";

// Keep public pages in their own lazy-loaded, CSS-scoped feature.
const Landing = lazy(() => import("./features/marketing/pages/HomePage"));
const PricingPage = lazy(() => import("./features/marketing/pages/PricingPage"));
const publicExport = (name) => lazy(() => import("./features/marketing/pages/ExplorePages").then(module => ({ default: module[name] })));
const companyExport = (name) => lazy(() => import("./features/marketing/pages/CompanyPages").then(module => ({ default: module[name] })));
const Demo = publicExport('DemoPage');
const FeaturesPage = publicExport('FeaturesPage');
const ProductPage = publicExport('ProductPage');
const SolutionsPage = publicExport('SolutionsPage');
const SolutionPage = publicExport('SolutionPage');
const FAQPage = publicExport('FAQPage');
const IntegrationsPage = publicExport('IntegrationsPage');
const SecurityPage = companyExport('SecurityPage');
const ContactPage = companyExport('ContactPage');
const AboutPage = companyExport('AboutPage');
const BlogPage = companyExport('BlogPage');
const BlogArticlePage = companyExport('ArticlePage');

const OTPVerificationPage = lazy(() => import("./features/auth/pages/OTPVerificationPage"));
const ResetPassword = lazy(() => import("./features/auth/pages/ResetPassword"));
const VendorRegistration = lazy(() => import("./features/dashboard/pages/vendor/VendorRegistration"));
const Page404 = publicExport("MissingPage");
// Removed MultiStepForm onboarding; business details can be managed from dashboard
const PolicyPage = lazy(() => import("./features/marketing/pages/PolicyPage"));

const ConnectShopifyRedirect = lazy(() => import("./features/shopify/pages/ConnectShopifyRedirect"));
const Layout = lazy(() => import("./features/dashboard/components/layout/Layout"));
const AdminPortal = lazy(() => import("./features/admin/AdminPortal"));
const Login = lazy(() => import("./features/auth/pages/Login"));
const ForgotPassword = lazy(() => import("./features/auth/pages/ForgotPassword"));
const AcceptInvite = lazy(() => import("./features/auth/pages/AcceptInvite"));
// Signup onboarding (business basics, plan, card or no card) and the screen an
// unpaid account is held at after its trial. Outside the dashboard Layout on
// purpose: both must work while the dashboard APIs answer 402.
const OnboardingPage = lazy(() => import("./features/onboarding/OnboardingPage"));
const PaymentRequiredPage = lazy(() => import("./features/onboarding/PaymentRequiredPage"));
// Selit's own support assistant. Mounted once, outside <Routes>, so it is on
// every page in both states: logged out it answers from the public material,
// logged in the server widens it to the portal guide and narrows it to the
// modules that vendor actually has. It hides itself on /admin.
const SupportBot = lazy(() => import("./shared/components/SupportBot"));

// Public shells manage their own theme; old landing styles are not injected.
const createLandingRoute = (Component) => (
  <Suspense fallback={<ThemedSuspense />}>
    <MarketingLanguageProvider><Component /></MarketingLanguageProvider>
  </Suspense>
);

// Opens its subtree in Portuguese. A first load of /br is already resolved to
// 'pt' by LanguageProvider from the URL; this covers arriving by client-side
// navigation, where the provider does not re-initialise. It runs once on mount
// rather than on every render, so a visitor who switches to English while on
// /br is not immediately flipped back.
const ForcePortuguese = ({ children }) => {
  const { setLanguage } = useLanguage();
  React.useEffect(() => {
    setLanguage('pt');
  }, [setLanguage]);
  return children;
};

const createBrazilLandingRoute = (Component) => (
  <Suspense fallback={<ThemedSuspense />}>
    <ForcePortuguese>
      <MarketingLanguageProvider><Component /></MarketingLanguageProvider>
    </ForcePortuguese>
  </Suspense>
);

function App() {
  return (
    <div className="main-app-most-outer-div">
      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        draggable={false}
        toastClassName="custom-toast"
      />
      <LanguageProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AccessibleNavigationAnnouncer />
          <TrailingSlashRedirect />
          <Routes>
            {/* Public platform, product and resource pages */}
            <Route path="/" element={createLandingRoute(Landing)} />
            <Route path="/landing-page" element={createLandingRoute(Landing)} />
            {/* Brazil entry point: the same landing page, always in Portuguese. */}
            <Route path="/br" element={createBrazilLandingRoute(Landing)} />
            <Route path="/about" element={createLandingRoute(AboutPage)} />
            <Route path="/blog" element={createLandingRoute(BlogPage)} />
            <Route path="/blog/:slug" element={createLandingRoute(BlogArticlePage)} />
            <Route path="/products" element={<Navigate to="/features" replace />} />
            <Route path="/products/:slug" element={createLandingRoute(ProductPage)} />
            <Route path="/solutions" element={createLandingRoute(SolutionsPage)} />
            <Route path="/solutions/:slug" element={createLandingRoute(SolutionPage)} />
            <Route path="/use-cases" element={<Navigate to="/solutions" replace />} />
            <Route path="/use-cases/ecommerce" element={<Navigate to="/solutions/online-retail" replace />} />

            {/* Landing page section routes */}
            <Route path="/demo" element={createLandingRoute(Demo)} />
            <Route path="/features" element={createLandingRoute(FeaturesPage)} />
            <Route path="/faq" element={createLandingRoute(FAQPage)} />
            <Route path="/integrations" element={createLandingRoute(IntegrationsPage)} />
            <Route path="/security" element={createLandingRoute(SecurityPage)} />
            <Route path="/pricing" element={createLandingRoute(PricingPage)} />
            <Route path="/contact" element={createLandingRoute(ContactPage)} />

            {/* App routes with ThemeProvider and DateFormatProvider */}
            <Route
              path="/*"
              element={
                <DateFormatProvider>
                  <Suspense fallback={<ThemedSuspense />}>
                    <Routes>
                      <Route path="/login" element={<Login user="Client" />} />
                      <Route path="/vendor/login" element={<Login user="Vendor" />} />
                      <Route path="/vendor/create-account" element={<VendorRegistration />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/verify-account" element={<OTPVerificationPage />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/accept-invite" element={<AcceptInvite />} />
                      <Route path="/connect-shopify" element={<ConnectShopifyRedirect />} />
                      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
                      <Route path="/billing/payment-required" element={<ProtectedRoute><PaymentRequiredPage /></ProtectedRoute>} />
                      {/* Redirect /dashboard to /app — backend OAuth callback lands here */}
                      <Route path="/dashboard" element={<Navigate to="/app/vendor/settings?tab=integrations" replace />} />
                      <Route
                        path="/app/*"
                        element={
                          <ProtectedRoute>
                            <Layout />
                          </ProtectedRoute>
                        }
                      />
                      {/* Admin portal (users.user_role = ADMIN) — standalone shell */}
                      <Route
                        path="/admin/*"
                        element={
                          <ProtectedRoute>
                            <AdminPortal />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/privacy-policy" element={createLandingRoute(PolicyPage)} />
                      <Route path="/privacy-policy/" element={createLandingRoute(PolicyPage)} />
                      <Route path="/cookie-policy" element={createLandingRoute(PolicyPage)} />
                      <Route path="/cookie-policy/" element={createLandingRoute(PolicyPage)} />
                      <Route path="/terms-of-service" element={createLandingRoute(PolicyPage)} />
                      <Route path="/terms-of-service/" element={createLandingRoute(PolicyPage)} />
                      <Route path="*" element={createLandingRoute(Page404)} />
                    </Routes>
                  </Suspense>
                </DateFormatProvider>
              }
            />
          </Routes>
          <Suspense fallback={null}>
            <SupportBot />
          </Suspense>
        </Router>
      </LanguageProvider>
    </div>
  );
}

export default App;
